import * as React from "react";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";
import { nx, NX_RING_CLIP, type NxSize } from "../lib/onyx-material";
import { Spinner } from "./Spinner";

/**
 * Button — the brand button in the Onyx "offset key" language (the same physical
 * key as `KeycapButton`, with the full Button API on top: 7 variants, 8 sizes,
 * `isLoading`, `asChild`). A solid notched block sits down-right BEHIND the key
 * (the travel path — a real sibling element, so it survives the notch `clip-path`
 * where a `box-shadow` is sliced); on `:active` the key translates flush onto it
 * with a springy bounce. This replaces the old pill + Framer scale-tap so every
 * button across the lib presses the same way.
 *
 * Variant → raise hierarchy:
 * - `default` / `gradient` — rich top-lit navy gradient, raised MOST (3px block).
 * - `secondary` / `destructive` — simpler, less raised (2px block).
 * - `outline` — flat bordered (ring only, no block); presses DOWN 1px.
 * - `ghost` — quiet (no ring, no block); presses DOWN 1px + soft hover.
 * - `link` — inline text link (no key, no notch, no press); unchanged role.
 * - **Icon-only sizes (`icon*`) override the hierarchy: ALWAYS flat**, whatever
 *   the variant (owner ruling, PR #256 / DESIGN.md "Buttons"): no travel block,
 *   1px press-down — a small icon control is an icon control first (the
 *   IconButton doctrine), whether the app renders it as a pill circle or a
 *   notched square.
 * Every variant but `ghost`/`link` carries the `nx-rim` keyline ring (a real
 * border is sliced at the notch, #158): ink at rest, flipping to the `--ring`
 * accent on focus with the form family's 180ms smooth-out ease — the same
 * border-anim NotchedInput's rim and the TagInput's `focus-within:border-ring`
 * carry, so buttons and inputs focus as one control language. Fills derive from
 * `--primary` / `--destructive` / `--foreground`, so an app's theme recolors it
 * with zero edits.
 *
 * Composition:
 * - `asChild` renders children through Radix Slot (e.g. wrap an `<a>` / router Link).
 * - `motion={false}` keeps the depth but disables the press ANIMATION (static key).
 * - `isLoading` renders a Spinner before children, forces `disabled`, sets `aria-busy`.
 * - `loadingText` swaps in copy while loading; falls back to `children`.
 * - `notch` picks the chamfer (`t` subtlest → `l` largest); default `b`.
 * - Defaults `type="button"` (safer than a bare button's native `submit`); pass
 *   `type="submit"` explicitly for a form-submit button. Stock `ui/button` keeps
 *   the native default, so set an explicit `type` on the brand Button in forms.
 */
export const buttonVariants = cva(
  [
    "group/nxbtn relative z-[1] inline-flex select-none cursor-pointer items-center justify-center gap-2 whitespace-nowrap border-0 font-medium leading-none",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
    // Clip-safe focus: a drop-shadow glow SURVIVES the notch clip-path, where a
    // `ring-*` box-shadow would be sliced at the chamfer (onyx-material.ts rule #2).
    // The glow is NotchedInput's exact one (3px @ 0.45) — the button and the form
    // family share a single focus weight (owner consistency ask, tags-input pair)
    // — and reads fine on the unclipped `link` too. The rim's matching
    // ink→accent flip lives on the rim span below.
    "focus-visible:outline-none focus-visible:[filter:drop-shadow(0_0_3px_hsl(var(--ring)/0.45))]",
    "disabled:pointer-events-none disabled:opacity-50",
  ].join(" "),
  {
    variants: {
      variant: {
        // Rich top-lit navy gradient (navy lightened toward white at the top →
        // navy), derived from --primary so it stays token-driven.
        default:
          "text-primary-foreground bg-[linear-gradient(176deg,color-mix(in_oklab,hsl(var(--primary))_84%,#fff),hsl(var(--primary)))]",
        gradient:
          "text-primary-foreground bg-[linear-gradient(176deg,color-mix(in_oklab,hsl(var(--primary))_84%,#fff),hsl(var(--primary)))]",
        // Surface face; theme-flipping tokens so it adapts light/dark.
        secondary: "bg-background text-foreground",
        destructive: "bg-destructive text-destructive-foreground",
        // Flat bordered, transparent fill (ring only, no travel block).
        outline: "bg-transparent text-foreground",
        // Quiet — no ring, no block; soft hover.
        ghost: "bg-transparent text-foreground hover:bg-foreground/[0.06]",
        // Inline text link — not a key (no notch/ring/press); h-auto via override below.
        link: "bg-transparent text-accent underline-offset-4 hover:underline",
      },
      size: {
        xs: "h-8 gap-1.5 px-2.5 text-xs [&_svg:not([class*='size-'])]:size-3.5",
        sm: "h-9 px-3 text-xs",
        default: "h-10 px-4 text-sm",
        lg: "h-12 px-7 text-[15px] font-semibold tracking-[0.2px]",
        icon: "h-10 w-10",
        "icon-xs": "h-8 w-8 [&_svg:not([class*='size-'])]:size-3.5",
        "icon-sm": "h-9 w-9",
        "icon-lg": "h-12 w-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

type ButtonElementProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color">;

export interface ButtonProps extends ButtonElementProps, VariantProps<typeof buttonVariants> {
  /** Render children via Radix Slot (no `<button>` wrapper). */
  asChild?: boolean;
  /** Keep the depth but disable the press ANIMATION (static key). Default true. */
  motion?: boolean;
  /** Render a Spinner before children, force `disabled`, set `aria-busy`. Ignored when `asChild`. */
  isLoading?: boolean;
  /** Optional copy to render while `isLoading`. Falls back to `children`. */
  loadingText?: React.ReactNode;
  /** Notch chamfer size. Default `b` (refined for a button). */
  notch?: NxSize;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = "default",
      size,
      notch = "b",
      asChild = false,
      motion: enableMotion = true,
      isLoading = false,
      loadingText,
      children,
      type,
      disabled,
      ...rest
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    const isLink = variant === "link";
    const isGhost = variant === "ghost";
    // default + gradient are the primary key (raised most, 3px). secondary +
    // destructive are simpler (2px). outline + ghost are flat (no block) but
    // press down 1px. link is not a key at all.
    const isPrimary = variant === "default" || variant === "gradient";
    // Icon-only sizes are ALWAYS flat, whatever the variant: per the IconButton
    // doctrine a small round-or-square icon control carries no offset block —
    // behind a 32px pill/circle the 3px block reads as a detached black
    // crescent, not a keycap base (PlatformQA#2829). It keeps the shared press
    // language by pressing DOWN 1px like every flat control. This also keeps
    // the icon sizes' square cva width on the face (no `w-full` swap), which is
    // what sizes them — a bare keyed wrapper used to shrink-wrap the glyph.
    const isIconOnly = size != null && size.startsWith("icon");
    const keyed =
      !isLink && !isIconOnly && (isPrimary || variant === "secondary" || variant === "destructive");
    const press = isLink
      ? ""
      : !keyed
        ? "active:translate-y-px"
        : isPrimary
          ? "active:translate-x-[3px] active:translate-y-[3px]"
          : "active:translate-x-[2px] active:translate-y-[2px]";
    // Springy overshoot bounce on release — shared by every keyed/flat variant —
    // COMPOSED with the 180ms ease-smooth-out focus-glow ride in ONE arbitrary
    // declaration: two transition-* utilities would fight over
    // `transition-property` (stylesheet order picks a winner), so both rides are
    // spelled per-property. The filter leg is NotchedInput's `transition-[filter]
    // duration-180 ease-smooth-out` (0.16,1,0.3,1), which stands alone when the
    // press animation is off (`motion={false}` stills the key, not the focus) and
    // on `link` (no press, glow only).
    const glowRide = "transition-[filter] duration-180 ease-smooth-out";
    const bounce = isLink
      ? ""
      : "[transition:transform_150ms_cubic-bezier(0.34,1.45,0.6,1),filter_180ms_cubic-bezier(0.16,1,0.3,1)]";

    if (asChild && isLoading && process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn(
        "Button: `isLoading` is ignored when `asChild` is true — Slot can only host a single child.",
      );
    }

    const effectiveDisabled = isLoading || disabled;
    const content =
      isLoading && !asChild ? (
        <>
          <Spinner size="sm" decorative />
          {loadingText ?? children}
        </>
      ) : (
        children
      );

    const face = (
      <Comp
        ref={ref as React.Ref<HTMLButtonElement>}
        className={cn(
          !isLink && nx[notch],
          buttonVariants({ variant, size }),
          // Inline link ignores the key box: drop the fixed height + tighten pad.
          isLink && "h-auto px-1",
          // The springy press (gated by `motion`); the travel distance varies by
          // raise level (3px primary / 2px secondary / 1px flat). The focus glow
          // eases regardless of `motion`.
          enableMotion && bounce ? bounce : glowRide,
          enableMotion && press,
          // Keyed variants wrap in a sizing span; the face fills it so a consumer
          // width utility on the wrapper (w-full/flex-1) reaches the key. Non-keyed
          // (outline/ghost/link) ARE the root, so they take `className` here.
          keyed ? "w-full" : className,
        )}
        type={asChild ? undefined : (type ?? "button")}
        {...(asChild ? {} : { disabled: effectiveDisabled, "aria-busy": isLoading || undefined })}
        {...rest}
      >
        {/* Ink keyline — a hollow ring on the SAME notch (NX_RING_CLIP) so the 1px
            border traces the notch instead of being sliced at the diagonal (#158).
            From `--foreground` so it stays neutral at rest; on the key's focus it
            flips to `--ring`, the SAME hairline→accent border flip NotchedInput's
            rim does (and the TagInput family's `focus-within:border-ring`), eased
            180ms smooth-out so buttons and inputs animate as one control language
            (owner consistency ask). Rest color is a CLASS (not inline style) so
            the focus override can win. Ghost + link have no ring. */}
        {!isGhost && !isLink && (
          <span
            aria-hidden
            className={cn(
              "nx-rim pointer-events-none absolute inset-0",
              "[--nx-rim:hsl(var(--foreground))]",
              "transition-colors duration-180 ease-smooth-out",
              "group-focus-visible/nxbtn:[--nx-rim:hsl(var(--ring))]",
            )}
            style={{ clipPath: NX_RING_CLIP[notch] } as React.CSSProperties}
          />
        )}
        <Slottable>{content}</Slottable>
      </Comp>
    );

    if (!keyed) return face;

    // The travel path: a solid notched block down-right, BEHIND the key. A sibling
    // (not a child) so it stays put while the key presses onto it. Dark ink so it
    // reads on the light page; a deep accent in dark.
    return (
      <span className={cn("relative inline-flex", className)}>
        <span
          aria-hidden
          className={cn(
            nx[notch],
            "pointer-events-none absolute inset-0 z-0 bg-[#06101e] dark:bg-[#0a2a5e]",
            isPrimary
              ? "translate-x-[3px] translate-y-[3px]"
              : "translate-x-[2px] translate-y-[2px]",
            effectiveDisabled && "opacity-50",
          )}
        />
        {face}
      </span>
    );
  },
);

Button.displayName = "Button";
